import React, {
	useEffect,
	useMemo,
	useState,
	Fragment,
} from 'react'
import { Badge, Dialog, Input, Tooltip } from 'components/ui';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { DataTable } from 'components/shared';
import isEmpty from 'lodash/isEmpty';
import { DoubleSidedImage } from 'components/shared';
import { useDispatch, useSelector } from 'react-redux';
import { getSocialNetworkList, setTableData } from '../store/dataSlice';
import { setSortedColumn } from '../store/stateSlice';
import useThemeClass from 'utils/hooks/useThemeClass';
import cloneDeep from 'lodash/cloneDeep';
import { toggleDeleteConfirmation } from '../store/stateSlice';
import { setSelectedProduct } from '../store/stateSlice';
import SocialNetworkDeleteConfirmation from './SocialNetworkDeleteConfirmation';
import { useTranslation } from 'react-i18next';
import { apiOrderSocialNetwork } from 'services/SocialNetworkService';
import SocialNetworkEdit from 'views/socialnetwork/SocialNetworkEdit';

const EditableCell = ({ value, row: { id, languageId }, updateData }) => {
	const [cellValue, setCellValue] = useState(value);

	useEffect(() => {
		setCellValue(value);
	}, [value, id, languageId]);

	const onBlur = () => {
		updateData(id, cellValue, languageId);
	};

	return (
		<Input
			className="border-transparent bg-transparent hover:border-gray-300 w-14 focus:bg-white"
			type="text"
			value={cellValue}
			onChange={(e) => setCellValue(e.target.value)}
			onBlur={onBlur}
		/>
	);
};

const ActionColumn = ({ row }) => {
	const { textTheme } = useThemeClass()
	const dispatch = useDispatch()
	const dialogState = useSelector((state) => state.SelectedSocialsForm.data.dialogState);
	const [editOpen, setEditOpen] = useState(false);
	const { t } = useTranslation();
	const isMobileScreen = window.innerWidth <= 992;
	const onEdit = () => {
		setEditOpen(true);
		dispatch(setSelectedProduct(row.id))
	}

	const onEditClose = () => {
		setEditOpen(false);
	}

	useEffect(() => {
		setState()
	}, [dialogState]);

	const setState = () => {
		if (dialogState === true) {
			setEditOpen(false)
		}
	}

	const onDelete = () => {
		dispatch(toggleDeleteConfirmation(true))
		dispatch(setSelectedProduct(row.id))
	}

	return (
		<Fragment>
			<Dialog
				width={'100%'}
				isOpen={editOpen}
				onClose={onEditClose}
				onRequestClose={onEditClose}
			>
				<h3 className="mb-5">{t('add-edit.editsocialnetwork')}</h3>
				<SocialNetworkEdit />
			</Dialog>
			<div className="flex flex-row justify-start text-lg">
				{isMobileScreen ? (
					<>
						<span className={`cursor-pointer p-2 hover:${textTheme}`} onClick={onEdit}>
							<HiOutlinePencil style={{ fontSize: '17px' }} />
						</span>

						<span className="cursor-pointer p-2 hover:text-red-500" onClick={onDelete}>
							<HiOutlineTrash style={{ fontSize: '17px' }} />
						</span>
					</>
				) : (
					<>
						<Tooltip title={t('translation.edit')} placement="bottom">
							<span className={`cursor-pointer p-2 hover:${textTheme}`} onClick={onEdit}>
								<HiOutlinePencil style={{ fontSize: '17px' }} />
							</span>
						</Tooltip>
						<Tooltip title={t('tooltip.Delete')} placement="bottom">
							<span className="cursor-pointer p-2 hover:text-red-500" onClick={onDelete}>
								<HiOutlineTrash style={{ fontSize: '17px' }} />
							</span>
						</Tooltip>
					</>
				)}
			</div>
		</Fragment>
	)
}

const SocialNetworkTable = () => {
	const dispatch = useDispatch()
	const { pageIndex, pageSize, sort, query, total, layoutId, componentLocationId } = useSelector((state) => state.socialnetworksList.data.tableData);
	const webLangId = useSelector((state) => state.selectLang.data.selectedLanguage);
	const filterData = useSelector((state) => state.socialnetworksList.data.filterData);
	const data = useSelector((state) => state.socialnetworksList.data.productList);
	const { t } = useTranslation();

	useEffect(() => {
		if (layoutId && layoutId !== 0) {
			fetchData()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageIndex, pageSize, sort, layoutId, componentLocationId, webLangId])

	const tableData = useMemo(() =>
		({ pageIndex, pageSize, sort, query, total, layoutId, componentLocationId, webLangId }),
		[pageIndex, pageSize, sort, query, total, layoutId, componentLocationId, webLangId]);

	const [dataLoaded, setDataLoaded] = useState(false);

	const fetchData = () => {
		dispatch(getSocialNetworkList({ pageIndex, pageSize, sort, query, filterData, layoutId, componentLocationId, webLangId }))
			.then(() => {
				setDataLoaded(true);
			});
	};

	const inventoryStatusColor = {
		true: { label: t('translation.po'), dotClass: 'bg-emerald-500', textClass: 'text-emerald-500' },
		false: { label: t('translation.jo'), dotClass: 'bg-red-500', textClass: 'text-red-500' },
	}

	const UpdateSocialNetworkOrder = async (data) => {
		const response = await apiOrderSocialNetwork(data)
		return response.data
	}

	const UpdateOrder = (id, value, languageId) => {
		const updatedData = {
			id: id,
			languageId: languageId,
			orderNo: value,
		};

		UpdateSocialNetworkOrder([updatedData]);
	};

	const columns = useMemo(() => [
		{
			Header: `${t('translation.nameLocaleTable')}`,
			accessor: 'name',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.name}</span>
				)
			},
		},
		{
			Header: `${t('translation.location')}`,
			accessor: 'componentLocation',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.componentLocation.titleSq}</span>
				)
			},
		},
		{
			Header: `${t('translation.link')}`,
			accessor: 'link',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.link}</span>
				)
			},
		},
		{
			Header: `${t('translation.path')}`,
			accessor: 'imgPath',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.imgPath}</span>
				)
			},
		},
		{
			Header: `${t('translation.html')}`,
			accessor: 'html',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.html}</span>
				)
			},
		},
		{
			Header: `${t('translation.active')}`,
			accessor: 'active',
			sortable: true,
			Cell: props => {
				const { active } = props.row.original
				return (
					<div className="flex items-center gap-2">
						<Badge className={inventoryStatusColor[active].dotClass} />
						<span className={`capitalize font-semibold ${inventoryStatusColor[active].textClass}`}>
							{inventoryStatusColor[active].label}
						</span>
					</div>
				)
			},
		},
		{
			Header: `${t('table.OrderNo')}`,
			accessor: 'orderNo',
			sortable: true,
			size: 50,
			Cell: props => {
				const { value } = props.cell;
				const row = props.row.original;
				return (
					<EditableCell
						value={value}
						row={row}
						column={props.column}
						updateData={UpdateOrder}
					/>
				);
			},
		},
		{
			Header: `${t('translation.action')}`,
			id: 'action',
			accessor: (row) => row,
			Cell: props => <ActionColumn row={props.row.original} />
		},
	], [])

	const onPaginationChange = page => {
		const newTableData = cloneDeep(tableData)
		newTableData.pageIndex = page
		dispatch(setTableData(newTableData))
	}

	const onSelectChange = value => {
		const newTableData = cloneDeep(tableData)
		newTableData.pageSize = Number(value)
		newTableData.pageIndex = 1
		dispatch(setTableData(newTableData))
	}

	const onSort = (sort, sortingColumn) => {
		const newTableData = cloneDeep(tableData)
		newTableData.sort = sort
		dispatch(setTableData(newTableData))
		dispatch(setSortedColumn(sortingColumn))
	}

	return (
		<Fragment>
			<div className="card-border">
				<DataTable
					columns={columns}
					data={data}
					skeletonAvatarColumns={[0]}
					skeletonAvatarProps={{ className: 'rounded-md' }}
					loading={!dataLoaded}
					pagingData={tableData}
					onPaginationChange={onPaginationChange}
					onSelectChange={onSelectChange}
					onSort={onSort}
				/>
				{(dataLoaded && isEmpty(data)) && (
					<div className="h-full w-full py-4 flex flex-col items-center justify-center">
						<DoubleSidedImage
							src="/img/others/img-2.png"
							darkModeSrc="/img/others/img-2-dark.png"
							alt={t('translation.notFound')}
						/>
						<h3 className="mt-8">{t('translation.notFound')}</h3>
					</div>
				)}
			</div>
			<SocialNetworkDeleteConfirmation />
		</Fragment>
	)
}

export default SocialNetworkTable